import { ReactNode, useState } from 'react'

type TooltipPosition = 'top' | 'bottom' | 'left' | 'right'

interface TooltipProps {
  /** One-line label. Keep it short — for richer help use InfoPopover. */
  content: ReactNode
  position?: TooltipPosition
  children: ReactNode
  className?: string
}

// Position of the bubble relative to the wrapped trigger.
const POSITION_CLASS: Record<TooltipPosition, string> = {
  top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
  bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
  left: 'right-full top-1/2 -translate-y-1/2 mr-2',
  right: 'left-full top-1/2 -translate-y-1/2 ml-2',
}

/**
 * Branded (Messaging Advisory blue) hover/focus tooltip for micro-help — "what
 * does this control do". Shows on hover AND keyboard focus for accessibility.
 * The bubble is `pointer-events-none` so it never intercepts clicks, and
 * absolutely positioned so it occupies zero layout width (safe in tight toolbars).
 * For multi-line concept help ("what is an element vs a template"), use
 * InfoPopover instead.
 */
export function Tooltip({ content, position = 'top', children, className = '' }: TooltipProps) {
  const [open, setOpen] = useState(false)

  return (
    <span
      className={`relative inline-flex ${className}`}
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      {children}
      {open && (
        <span
          role="tooltip"
          className={`pointer-events-none absolute z-50 max-w-xs whitespace-nowrap rounded-md bg-primary-900 px-2.5 py-1.5 text-[11px] font-medium text-white shadow-lg ${POSITION_CLASS[position]}`}
        >
          {content}
        </span>
      )}
    </span>
  )
}
